import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Wrench, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PersistentMfaRotation — distinct from PersistentMfaSetup (iter105):
 *   • iter105 setup = admin RESET the user's MFA (old TOTP wiped,
 *     user enrolls fresh)
 *   • this rotation = admin FORCED rotation (old TOTP still valid;
 *     user must enter current password + current code to start;
 *     pending secret expires after 2 min)
 */
const SESSION_FLAG = '__orb_mfa_rotation_in_progress';
const ROTATION_TTL_MS = 120 * 1000;

export default function PersistentMfaRotation() {
  const { user, refreshUser, api, logout } = useAuth();
  const required = user?.mfa_rotation_required;

  const [phase, setPhase] = useState('challenge'); // challenge | setup | done
  const [currentPwd, setCurrentPwd] = useState('');
  const [currentTotp, setCurrentTotp] = useState('');
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Refresh detection → logout
  useEffect(() => {
    if (!required) return;
    const flag = sessionStorage.getItem(SESSION_FLAG);
    if (flag) {
      sessionStorage.removeItem(SESSION_FLAG);
      toast.error('Session ended — please log back in to continue');
      logout();
      return;
    }
    sessionStorage.setItem(SESSION_FLAG, String(Date.now()));
    setStartedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  useEffect(() => {
    if (!startedAt) return;
    if (now - startedAt >= ROTATION_TTL_MS && phase !== 'done') {
      sessionStorage.removeItem(SESSION_FLAG);
      toast.error('Time expired — please log back in');
      logout();
    }
  }, [now, startedAt, logout, phase]);

  useEffect(() => {
    if (!required) return;
    const blockKeys = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener('keydown', blockKeys, true);
    return () => window.removeEventListener('keydown', blockKeys, true);
  }, [required]);

  const startSetup = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/me/security/start-mfa-rotation', {
        current_password: currentPwd,
        current_totp_code: currentTotp,
      });
      setQr(res.data?.qr_code);
      setSecret(res.data?.secret);
      setPhase('setup');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start');
    } finally { setSubmitting(false); }
  };

  const finalize = async () => {
    setSubmitting(true);
    try {
      await api.post('/me/security/complete-mfa-rotation', { code: newCode });
      setPhase('done');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not finalize');
    } finally { setSubmitting(false); }
  };

  const close = async () => {
    sessionStorage.removeItem(SESSION_FLAG);
    await refreshUser();
  };

  if (!user || !required) return null;

  const remaining = startedAt ? Math.max(0, Math.ceil((ROTATION_TTL_MS - (now - startedAt)) / 1000)) : 120;
  const mm = String(Math.floor(remaining / 60)).padStart(1, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-4"
      style={{ backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
      data-testid="persistent-mfa-rotation-overlay"
    >
      <div className="w-full max-w-md rounded-2xl bg-background border border-red-500/40 shadow-2xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 dark:bg-amber-900/40 p-2">
            <Wrench className="w-5 h-5 text-amber-600 dark:text-amber-300" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-base">Maintenance Time!</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Please reset your Authenticator credentials to keep your account safe.
            </p>
          </div>
          {phase !== 'done' && (
            <span className="text-[11px] font-mono font-bold text-red-600 dark:text-red-300" data-testid="mfa-rotation-timer">
              {mm}:{ss}
            </span>
          )}
        </div>

        {phase === 'challenge' && (
          <div className="space-y-3" data-testid="mfa-rotation-challenge">
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Current Password</Label>
              <Input type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} data-testid="mfa-rotation-current-pwd" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Current Authenticator Code</Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={currentTotp}
                onChange={(e) => setCurrentTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono text-center tracking-widest"
                data-testid="mfa-rotation-current-totp"
              />
            </div>
            <Button
              className="w-full bg-[#586D52] hover:bg-[#475a43]"
              onClick={startSetup}
              disabled={submitting || !currentPwd || currentTotp.length !== 6}
              data-testid="mfa-rotation-challenge-submit"
            >
              {submitting ? 'Verifying…' : 'Continue'}
            </Button>
          </div>
        )}

        {phase === 'setup' && (
          <div className="space-y-3" data-testid="mfa-rotation-setup">
            {qr && (
              <div className="flex justify-center bg-white p-3 rounded-lg">
                <img src={qr} alt="QR" className="w-44 h-44" data-testid="mfa-rotation-qr" />
              </div>
            )}
            {secret && (
              <div className="text-[11px] text-center text-muted-foreground flex items-center justify-center gap-2">
                <code className="bg-muted px-1.5 py-0.5 rounded font-mono" data-testid="mfa-rotation-secret">{secret}</code>
                <button
                  className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"
                  onClick={() => { navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                >
                  {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">6-digit Code from New Setup</Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono text-center tracking-widest"
                data-testid="mfa-rotation-new-code"
              />
            </div>
            <Button
              className="w-full bg-[#586D52] hover:bg-[#475a43]"
              onClick={finalize}
              disabled={submitting || newCode.length !== 6}
              data-testid="mfa-rotation-finalize"
            >
              {submitting ? 'Saving…' : 'Activate'}
            </Button>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3" data-testid="mfa-rotation-done">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 space-y-1">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">New credentials saved.</p>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400">Remove the old entry from your authenticator app to avoid confusion.</p>
            </div>
            <Button
              className="w-full bg-[#586D52] hover:bg-[#475a43]"
              onClick={close}
              data-testid="mfa-rotation-return-home"
            >
              Return to Home
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
